import CApiHelper from '@frontrow/console/src/entities/helpers/common-api-helper'
import {ajaxJsonCall, ajaxCall} from '@freckle/ajax'

import {
  consoleFancyClient,
  extractData,
  type fancyOperations,
  type FancyOk
} from '@frontrow/console/src/open-api/clients'

import {
  type SchoolAdminUpdateT,
  type DetailedSchoolAdminT
} from '@frontrow/console/src/models/school-admin'
import {type FetchDataParamsT} from '@frontrow/console/src/components/common/data-table'
import {createDatatableUrl} from '@frontrow/console/src/helpers/data-table'
import {type BasicSchoolAdminT} from '@frontrow/console/src/models/school-admin'

// Not migrated to consoleFancyClient: createDatatableUrl builds an arbitrary
// query string shared by every domain's datatable, and mapping its filters
// onto openapi-fetch's typed `params.query` deserves its own pass across all
// of those domains rather than a one-off cast here.
export async function fetchSchoolAdmins(
  fetchDataParams?: FetchDataParamsT | null
): Promise<Array<DetailedSchoolAdminT>> {
  const baseUrl = CApiHelper.fancyPaths.v2.school_admins._()
  const url = createDatatableUrl(baseUrl, fetchDataParams)
  return ajaxJsonCall<Array<DetailedSchoolAdminT>>({url, method: 'GET'})
}

export async function postSchoolAdmin(
  newSchoolAdminData: BasicSchoolAdminT
): Promise<FancyOk<fancyOperations['postSchoolAdminsR']>> {
  return extractData(
    await consoleFancyClient.POST('/2/school-admins', {
      body: {
        firstName: newSchoolAdminData.firstName,
        lastName: newSchoolAdminData.lastName,
        position: newSchoolAdminData.position,
        email: newSchoolAdminData.email,
        confirmed: newSchoolAdminData.confirmed,
        schoolId: newSchoolAdminData.schoolId,
        districtId: newSchoolAdminData.districtId,
        language: newSchoolAdminData.language
      }
    })
  )
}

export async function patchSchoolAdmin(
  schoolAdminId: number,
  schoolAdminToUpdate: SchoolAdminUpdateT
): Promise<FancyOk<fancyOperations['patchSchoolAdminR']>> {
  return extractData(
    await consoleFancyClient.PATCH('/2/school-admins/{SchoolAdminP-SchoolAdminId}', {
      params: {path: {'SchoolAdminP-SchoolAdminId': schoolAdminId}},
      body: schoolAdminToUpdate
    })
  )
}

export function addSchoolAdminDistrict(districtId: number, schoolAdminId: number): Promise<string> {
  const url =
    CApiHelper.fancyPaths.v2.districts.district.district_school_admins.district_school_admin.school_memberships(
      districtId,
      schoolAdminId
    )
  return ajaxCall({
    url,
    method: 'POST',
    contentType: 'text/plain',
    dataType: 'text'
  })
}

export function deleteSchoolAdminDistrict(
  districtId: number,
  schoolAdminId: number
): Promise<string> {
  const url =
    CApiHelper.fancyPaths.v2.districts.district.district_school_admins.district_school_admin.school_memberships(
      districtId,
      schoolAdminId
    )
  return ajaxCall({
    url,
    method: 'DELETE',
    contentType: 'text/plain',
    dataType: 'text'
  })
}

export function addSchoolAdminSchools(
  schoolIds: Array<number>,
  schoolAdminId: number
): Promise<string> {
  const url = CApiHelper.fancyPaths.v2.schools.school_admins_school_memberships(
    schoolIds,
    schoolAdminId
  )
  return ajaxCall({
    url,
    method: 'POST',
    contentType: 'text/plain',
    dataType: 'text'
  })
}

export function deleteSchoolAdminSchools(
  schoolIds: Array<number>,
  schoolAdminId: number
): Promise<string> {
  const url = CApiHelper.fancyPaths.v2.schools.school_admins_school_memberships(
    schoolIds,
    schoolAdminId
  )
  return ajaxCall({
    url,
    method: 'DELETE',
    contentType: 'text/plain',
    dataType: 'text'
  })
}

export async function sendImpersonateSchoolAdmin(schoolAdminEmail: string): Promise<unknown> {
  const {data} = await consoleFancyClient.POST('/2/admins/impersonations', {
    body: {schoolAdminEmail}
  })
  return data
}

/**
 * DELETE a School Admin
 *
 * Fails if any outstanding Math or District assessments remain associated with the admin.
 */
export async function deleteSchoolAdmin(schoolAdminId: number): Promise<unknown> {
  const {data} = await consoleFancyClient.DELETE('/2/school-admins/{SchoolAdminP-SchoolAdminId}', {
    params: {path: {'SchoolAdminP-SchoolAdminId': schoolAdminId}}
  })
  return data
}
